import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Input, Label, Modal, Textarea } from '../../../shared/components';
import { 
  Save,
  Loader2
} from 'lucide-react';
import { useEventManagement } from '../../../shared/hooks';

/**
 * Formulario de evento optimizado para modal
 * Maneja creación y edición de eventos
 * Adaptado a los campos del CreateEventDto del backend
 */
const EventFormModal = ({ 
  isOpen, 
  onClose, 
  event = null, 
  mode = 'create',
  onSuccess,
  createEvent,
  updateEvent
}) => {
  const { loading, error, clearError } = useEventManagement();

  // Estados del formulario - adaptado al CreateEventDto del backend
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    eventDate: '',
    eventTime: '',
    saleStartDate: '',
    saleStartTime: '',
    venue: '',
    venueAddress: '',
    status: 'upcoming',
    isActive: true,
    isOpenSale: false,
    isCarrera: false,
    maxTicketsPerCode: 6
  });

  // Estados de validación
  const [errors, setErrors] = useState({});

  // Estado inicial del formulario
  const initialFormData = useMemo(() => ({
    title: '',
    description: '',
    eventDate: '',
    eventTime: '',
    saleStartDate: '',
    saleStartTime: '',
    status: 'upcoming',
    isActive: true,
    isOpenSale: false,
    isCarrera: false,
    maxTicketsPerCode: 6
  }), []);

  // Función para limpiar el formulario
  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
    clearError();
  }, [clearError, initialFormData]);

  // Inicializar formulario con datos del evento si está en modo edición
  useEffect(() => {
    if (event && mode === 'edit') {
      setFormData({
        title: event.title || '',
        description: event.description || '',
        eventDate: event.eventDate ? new Date(event.eventDate).toISOString().split('T')[0] : '',
        eventTime: event.eventTime || '',
        saleStartDate: event.saleStartDate ? new Date(event.saleStartDate).toISOString().split('T')[0] : '',
        saleStartTime: event.saleStartDate ? new Date(event.saleStartDate).toTimeString().slice(0, 5) : '',
        status: event.status || 'upcoming',
        isActive: event.isActive !== undefined ? event.isActive : true,
        isOpenSale: event.isOpenSale !== undefined ? event.isOpenSale : false,
        isCarrera: event.isCarrera !== undefined ? event.isCarrera : false,
        maxTicketsPerCode: event.maxTicketsPerCode !== undefined ? event.maxTicketsPerCode : 6
      });
    } else if (event && mode === 'view') {
      setFormData({
        title: event.title || '',
        description: event.description || '',
        eventDate: event.eventDate ? new Date(event.eventDate).toISOString().split('T')[0] : '',
        eventTime: event.eventTime || '',
        saleStartDate: event.saleStartDate ? new Date(event.saleStartDate).toISOString().split('T')[0] : '',
        saleStartTime: event.saleStartDate ? new Date(event.saleStartDate).toTimeString().slice(0, 5) : '',
        status: event.status || 'upcoming',
        isActive: event.isActive !== undefined ? event.isActive : true,
        isOpenSale: event.isOpenSale !== undefined ? event.isOpenSale : false,
        isCarrera: event.isCarrera !== undefined ? event.isCarrera : false,
        maxTicketsPerCode: event.maxTicketsPerCode !== undefined ? event.maxTicketsPerCode : 6
      });
    } else {
      // Resetear formulario para creación
      resetForm();
    }
  }, [event, mode, clearError, resetForm]);

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};

    // Validar título
    if (!formData.title.trim()) {
      newErrors.title = 'El título es requerido';
    }

    // Validar fecha del evento
    if (!formData.eventDate) {
      newErrors.eventDate = 'La fecha del evento es requerida';
    } else {
      const selectedDate = new Date(formData.eventDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // if (selectedDate < today) {
      //   newErrors.eventDate = 'La fecha del evento no puede ser anterior a hoy';
      // }
    }

    // Validar hora del evento
    if (!formData.eventTime) {
      newErrors.eventTime = 'La hora del evento es requerida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar cambios en el formulario
  const handleInputChange = (field, value) => {
    if (mode === 'view') return; // No permitir cambios en modo visualización
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Limpiar errores previos
    setErrors({});
    
    if (!validateForm()) {
      return;
    }

    try {
      // Preparar datos para envío
      const submitData = { ...formData };
      
      // Combinar fecha y hora de inicio de venta si están presentes
      if (submitData.saleStartDate && submitData.saleStartTime) {
        const saleStartDateTime = new Date(`${submitData.saleStartDate}T${submitData.saleStartTime}`);
        submitData.saleStartDate = saleStartDateTime.toISOString();
        delete submitData.saleStartTime; // No enviar este campo al backend
      } else {
        // Si no hay fecha o hora, no enviar campo de inicio de venta
        delete submitData.saleStartDate;
        delete submitData.saleStartTime;
      }
      
      let result;
      
      if (mode === 'create') {
        result = await createEvent(submitData);
      } else {
        result = await updateEvent(event.id, submitData);
      }

      if (result) {
        onSuccess?.(result, mode);
        resetForm();
        onClose();
      }
    } catch (err) {
      // Manejar errores específicos por campo
      const errorMessage = err.message || 'Error desconocido';
      
      // Detectar errores específicos de duplicación de fecha y hora
      if (errorMessage.includes('fecha') && errorMessage.includes('hora')) {
        setErrors(prev => ({
          ...prev,
          eventDate: 'Ya existe un evento programado para esta fecha y hora.',
          eventTime: 'Ya existe un evento programado para esta fecha y hora.'
        }));
      } else {
        // Error general
        setErrors(prev => ({
          ...prev,
          general: errorMessage
        }));
      }
      
      // NO cerrar el modal cuando hay errores
      // El usuario debe poder corregir los errores
    }
  };

  // Manejar cierre del modal
  const handleClose = () => {
    // Solo permitir cerrar si no está cargando
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  const getModalTitle = () => {
    switch (mode) {
      case 'create':
        return 'Crear Nuevo Evento';
      case 'edit':
        return 'Editar Evento';
      case 'view':
        return 'Ver Detalles del Evento';
      default:
        return 'Evento';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={getModalTitle()}
      size="lg"
      closeOnOverlayClick={!loading}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Título */}
        <div>
          <Label htmlFor="title">Título del Evento *</Label>
          <Input
            id="title"
            type="text"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="Ingresa el título del evento"
            className={`${errors.title ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
            disabled={loading || mode === 'view'}
          />
          {errors.title && (
            <div className="flex items-center mt-1">
              <svg className="h-4 w-4 text-red-500 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-red-500 text-sm">{errors.title}</p>
            </div>
          )}
        </div>

        {/* Descripción */}
        <div>
          <Label htmlFor="description">Descripción</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Describe el evento..."
            rows={3}
            disabled={loading || mode === 'view'}
          />
        </div>

        {/* Fecha y Hora */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Fecha */}
          <div>
            <Label htmlFor="eventDate">Fecha del Evento *</Label>
            <Input
              id="eventDate"
              type="date"
              value={formData.eventDate}
              onChange={(e) => handleInputChange('eventDate', e.target.value)}
              className={`${errors.eventDate ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
              disabled={loading || mode === 'view'}
            />
            {errors.eventDate && (
              <div className="flex items-center mt-1">
                <svg className="h-4 w-4 text-red-500 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-red-500 text-sm">{errors.eventDate}</p>
              </div>
            )}
          </div>

          {/* Hora */}
          <div>
            <Label htmlFor="eventTime">Hora del Evento *</Label>
            <Input
              id="eventTime"
              type="time"
              value={formData.eventTime}
              onChange={(e) => handleInputChange('eventTime', e.target.value)}
              className={`${errors.eventTime ? 'border-red-500 focus:ring-red-500 focus:ring-red-500' : ''}`}
              disabled={loading || mode === 'view'}
            />
            {errors.eventTime && (
              <div className="flex items-center mt-1">
                <svg className="h-4 w-4 text-red-500 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-red-500 text-sm">{errors.eventTime}</p>
              </div>
            )}
          </div>
        </div>

        {/* Fecha de Inicio de Venta */}
        <div>
          <Label htmlFor="saleStartDate">Fecha de Inicio de Venta</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              id="saleStartDate"
              type="date"
              value={formData.saleStartDate || ''}
              onChange={(e) => handleInputChange('saleStartDate', e.target.value)}
              disabled={loading || mode === 'view'}
              placeholder="Selecciona fecha de inicio de venta"
            />
            <Input
              id="saleStartTime"
              type="time"
              value={formData.saleStartTime || ''}
              onChange={(e) => handleInputChange('saleStartTime', e.target.value)}
              disabled={loading || mode === 'view'}
              placeholder="Selecciona hora de inicio de venta"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Deja vacío para permitir venta inmediata sin restricciones
          </p>
        </div>


        {/* Estado y Activo */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Estado */}
          <div>
            <Label htmlFor="status">Estado del Evento</Label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={loading || mode === 'view'}
            >
              <option value="upcoming">Próximo</option>
              <option value="completed">Completado</option>
              <option value="cancelled">Cancelado</option>
            </select>
          </div>

          {/* Activo */}
          <div className="flex items-center">
            <input
              id="isActive"
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => handleInputChange('isActive', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              disabled={loading || mode === 'view'}
            />
            <Label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
              Evento Habilitado
            </Label>
          </div>
        </div>

        {/* Tipo de Venta */}
        <div className="flex items-center">
          <input
            id="isOpenSale"
            type="checkbox"
            checked={formData.isOpenSale}
            onChange={(e) => handleInputChange('isOpenSale', e.target.checked)}
            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
            disabled={loading || mode === 'view'}
          />
          <Label htmlFor="isOpenSale" className="ml-2 text-sm text-gray-700">
            Venta Global (Sin límite de entradas por código)
          </Label>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Si está marcado, se requiere código de acceso pero no hay límite en la cantidad de entradas por código. 
          Si no está marcado, se aplica el límite configurado de entradas por código de acceso.
        </p>

        {/* Evento de Carrera */}
        <div className="flex items-center mt-4">
          <input
            id="isCarrera"
            type="checkbox"
            checked={formData.isCarrera}
            onChange={(e) => handleInputChange('isCarrera', e.target.checked)}
            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
            disabled={loading || mode === 'view'}
          />
          <Label htmlFor="isCarrera" className="ml-2 text-sm text-gray-700">
            Evento de Carrera
          </Label>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Si está marcado, los avisos de inicio de venta se envían con la plantilla de WhatsApp de carrera.
        </p>

        {/* Campo para máximo de entradas por código */}
        <div className="mt-4">
          <Label htmlFor="maxTicketsPerCode" className="block text-sm font-medium text-gray-700 mb-2">
            Máximo de entradas por código de acceso
          </Label>
          <input
            id="maxTicketsPerCode"
            type="number"
            min="1"
            max="50"
            value={formData.maxTicketsPerCode}
            onChange={(e) => handleInputChange('maxTicketsPerCode', parseInt(e.target.value) || 6)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={loading || mode === 'view' || formData.isOpenSale}
            placeholder="6"
          />
          <p className="text-xs text-gray-500 mt-1">
            Número máximo de entradas que se pueden comprar con un código de acceso. 
            {formData.isOpenSale && ' (Deshabilitado cuando la venta es global)'}
          </p>
        </div>

        {/* Error general */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-300 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error del sistema</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Error general del formulario */}
        {errors.general && (
          <div className="p-4 bg-red-50 border border-red-300 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error de validación</h3>
                <p className="text-sm text-red-700 mt-1">{errors.general}</p>
              </div>
            </div>
          </div>
        )}

        {/* Indicador de errores en campos */}
        {(Object.keys(errors).length > 0 && !errors.general && !error) && (
          <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-yellow-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-yellow-800">Por favor, corrige los errores en los campos marcados</span>
            </div>
          </div>
        )}

        {/* Botones */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            onClick={handleClose}
            variant="outline"
            disabled={loading}
            className="flex-1"
          >
            Cancelar
          </Button>
          {mode !== 'view' && (
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {mode === 'create' ? 'Crear Evento' : 'Guardar Cambios'}
                </>
              )}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default EventFormModal;